import {
  type FtueHostAdapter,
  FtueHostProvider,
  type FtueSubscription,
  OnboardingProvider,
} from '@bb/onboarding-core';
import { useAuth } from '@bb/session-manager';
import type { AxiosInstance } from 'axios';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { getSubscriptionStatus, getSubscriptionUsage } from '../../requests/subscription';

// biome-ignore lint/suspicious/noExplicitAny: session-manager useAuth returns dynamic legacy shapes
type AnyData = any;

/**
 * Web (bbcedit) host wiring for the shared FTUE (@bb/onboarding-core). The web is
 * upload-driven and has no live rendered document, so the adapter omits
 * selectInDocument / getSelectionText — the shared review UI renders rule source text
 * inline and hides the live-selection quick-actions. getDocument returns null (the
 * Upload step's file picker supplies contracts).
 *
 * Auth is provided as capabilities: bbAxios for normal calls, fetchA (session-manager's
 * authed fetch) for the NDJSON streamers. The web runs ON the tenant origin, so a
 * `/api/...` path is same-origin — no tenant-URL building needed.
 */
export function WebFtueProvider({ children }: { children: ReactNode }) {
  const auth = useAuth() as AnyData;
  const { bbAxios, fetchA, isAuthenticated, isLoading } = auth;

  const isReady = !isLoading && !!isAuthenticated;
  // The web app is served from the tenant origin (https://<slug>.blackboiler.com),
  // so the subdomain is the storage namespace (mirrors the add-in's per-host key).
  const storageNamespace = window.location.hostname.split('.')[0] ?? 'web';

  // Subscription is read only for the playbook-entitlement gate; fetch it lazily.
  const [subscription, setSubscription] = useState<FtueSubscription | null>(null);
  useEffect(() => {
    if (!isReady) {
      return;
    }
    Promise.all([getSubscriptionStatus(), getSubscriptionUsage()])
      .then(([status, usage]) => {
        setSubscription({
          entitlements: status?.entitlements ?? undefined,
          usage: usage ?? status?.usage ?? undefined,
        });
      })
      .catch(() => {
        // Non-fatal: the FTUE gate treats a missing subscription as "no limit".
        setSubscription(null);
      });
  }, [isReady]);

  const adapter = useMemo<FtueHostAdapter>(
    () => ({
      bbAxios: bbAxios as AxiosInstance,
      // Same-origin on the tenant host — pass the path straight through; fetchA attaches
      // the bearer + handles refresh. No selectInDocument/getSelectionText: web has no
      // live document (capability-branch → inline render).
      authedFetch: (path, init) => (fetchA as typeof fetch)(path, init),
      handleUnauthorized: () => auth.signOut?.(),
      isAuthError: (error) =>
        (error as AnyData)?.response?.status === 401 ||
        (error as AnyData)?.response?.status === 403,
      subscription,
      getDocument: async () => null,
    }),
    [bbAxios, fetchA, auth, subscription]
  );

  return (
    <FtueHostProvider adapter={adapter}>
      <OnboardingProvider isReady={isReady} storageNamespace={storageNamespace}>
        {children}
      </OnboardingProvider>
    </FtueHostProvider>
  );
}
